import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MARRIAGE_FAQ_CATEGORIES } from "@/content/marriage-corpus/faq-defs";

export const Route = createFileRoute("/marriage/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/marriage-corpus");
    return { faqs: m.MARRIAGE_FAQ_TH };
  },
  component: MarriageFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `จดทะเบียนสมรสไทย–ต่างชาติ — FAQ ${faqs.length} ข้อ อ้างอิงราชการ`,
        description:
          "คำถามที่พบบ่อยเรื่องจดทะเบียนสมรสไทย–ต่างชาติ หนังสือรับรองสถานภาพจากสถานทูต การแปลและรับรองกรมการกงสุล สัญญาก่อนสมรส หย่า เปลี่ยนนามสกุล วีซ่าฐานสมรส และงานทะเบียนบุตร",
        path: "/marriage/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/marriage/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "จดทะเบียนสมรส FAQ", url: `${site}/marriage/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/marriage/faq")) },
      ],
    };
  },
});

function MarriageFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>จดทะเบียนสมรส FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          จดทะเบียนสมรสไทย–ต่างชาติ และงานทะเบียนราษฎร — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5 พระราชบัญญัติแก้ไขเพิ่มเติมฯ (สมรสเท่าเทียม) ที่มีผล 22 มกราคม 2568
          ระเบียบของกรมการปกครอง (bora.dopa.go.th) กรมการกงสุล (consular.mfa.go.th) และสำนักงานตรวจคนเข้าเมือง (immigration.go.th)
          ข้อมูลนี้เป็นข้อมูลทั่วไป ไม่ใช่ความเห็นทางกฎหมายเฉพาะกรณี และไม่รับประกันผลการพิจารณาของเจ้าหน้าที่
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/marriage/faq" className="text-primary underline">Thai–foreign marriage registration FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {MARRIAGE_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/marriage/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/translation/faq">แปลเอกสารรับรอง</a></li>
            <li><a className="text-primary underline" href="/consular-legalization/faq">นิติกรณ์กรมการกงสุล</a></li>
            <li><a className="text-primary underline" href="/visa/faq">วีซ่าและการอยู่ต่อ</a></li>
            <li><a className="text-primary underline" href="/notary/faq">Notary Public / หนังสือมอบอำนาจ</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสาร ลำดับขั้นตอน และกรอบเวลาได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="marriage" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
